import { useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Pencil, Repeat, SkipForward, Square, Trash2 } from "lucide-react";
import { api, errText } from "@/lib/api";
import { fmtRange, fmtRelative, tzAbbr, tzCity, viewerTz } from "@/lib/time";

const REPEAT = { daily: "Every day", weekly: "Every week", monthly: "Every month" };
const iconBtn = "flex items-center gap-1.5 border border-black/15 bg-white px-3 py-2 text-xs font-semibold transition-colors hover:border-[#0A0A0A]";

const WindowCard = ({ w, onEdit, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const next = Array.isArray(w.next) ? w.next : [];
  const first = next[0];
  const mine = viewerTz();
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); } };
  const del = () => { if (window.confirm(`Delete “${w.title}”${w.repeat !== "none" ? " and all its repeats" : ""}?`)) act(() => api.delete(`/maintenance/windows/${w.id}`), "Maintenance deleted"); };

  return (
    <li data-testid={`window-${w.id}`} className={`border bg-white p-5 ${first?.active ? "border-[#E62314]" : "border-black/15"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 font-semibold">
            {w.title}
            {first?.active && <span className="bg-[#E62314] px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white" data-testid={`window-${w.id}-running`}>Running now</span>}
            {w.repeat !== "none" && <span className="flex items-center gap-1 bg-[#F3F2EF] px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider"><Repeat className="h-3 w-3" />{REPEAT[w.repeat]}</span>}
          </p>
          {first ? (
            <>
              <p className="mt-1.5 text-sm" data-testid={`window-${w.id}-next`}>{fmtRange(first.start, first.end, w.timezone)} <span className="text-[#57534E]">· {tzCity(w.timezone)} ({tzAbbr(first.start, w.timezone)})</span></p>
              {w.timezone !== mine && <p className="text-xs text-[#57534E]">Your time: {fmtRange(first.start, first.end, mine)} ({tzAbbr(first.start, mine)})</p>}
              <p className="mt-1 text-xs font-semibold text-[#E62314]">{first.active ? `Ends ${fmtRelative(first.end)}` : `Starts ${fmtRelative(first.start)}`}</p>
              {next.length > 1 && <p className="mt-2 text-xs text-[#57534E]">Then: {next.slice(1).map((o) => fmtRange(o.start, o.end, w.timezone)).join(" · ")}</p>}
              {w.repeat_until && <p className="mt-1 text-xs text-[#57534E]">Repeats until {w.repeat_until}</p>}
            </>
          ) : <p className="mt-1.5 text-sm text-[#57534E]">Finished. No more dates coming up.</p>}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {first?.active && <button disabled={busy} onClick={() => act(() => api.post(`/maintenance/windows/${w.id}/skip`, { start: first.start }), "Maintenance ended")} className={iconBtn} data-testid={`window-${w.id}-end-now`}><Square className="h-3.5 w-3.5" />End now</button>}
          {first && !first.active && <button disabled={busy} onClick={() => act(() => api.post(`/maintenance/windows/${w.id}/skip`, { start: first.start }), "That date is skipped")} className={iconBtn} data-testid={`window-${w.id}-skip`}><SkipForward className="h-3.5 w-3.5" />{w.repeat === "none" ? "Cancel" : "Skip next"}</button>}
          <button onClick={() => onEdit(w)} className={iconBtn} data-testid={`window-${w.id}-edit`}><Pencil className="h-3.5 w-3.5" />Edit</button>
          <button disabled={busy} onClick={del} className={iconBtn} data-testid={`window-${w.id}-delete`}><Trash2 className="h-3.5 w-3.5" />Delete</button>
        </div>
      </div>
    </li>
  );
};

export const ScheduleList = ({ windows, onNew, onEdit, onChanged }) => (
  <section data-testid="maintenance-schedule">
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight">Scheduled maintenance</h2>
        <p className="mt-1 text-sm text-[#57534E]">Starts and ends automatically, one-off or repeating.</p>
      </div>
      <button onClick={onNew} data-testid="maintenance-schedule-new" className="flex items-center gap-2 bg-[#0A0A0A] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#E62314]"><CalendarPlus className="h-4 w-4" />Schedule maintenance</button>
    </div>
    {windows.length ? (
      <ul className="mt-5 space-y-3">{windows.map((w) => <WindowCard key={w.id} w={w} onEdit={onEdit} onChanged={onChanged} />)}</ul>
    ) : (
      <p className="mt-5 border border-dashed border-black/20 p-8 text-center text-sm text-[#57534E]" data-testid="maintenance-schedule-empty">Nothing scheduled.</p>
    )}
  </section>
);
